/*
 * Purpose: Admin API client — analytics, AI insights, Work Engineer
 * Last modified: 2026-07-01 | Agent
 * Completeness: 96
 */
import type {
  AiAnalyticsInsight,
  AnalyticsSummary,
  EngineerDiagnosis,
  EngineerJob,
  PricingSubheadExperimentSummary,
} from '@/lib/siteAnalyticsTypes';
import { apiUrl } from '@/lib/apiBase';
import { AUTH_CONFIG } from '@/lib/auth-config';
import {
  buildLocalAnalyticsSummary,
  buildPricingSubheadExperimentSummary,
} from '@/lib/localAnalyticsBuffer';

async function adminFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(apiUrl(path), {
    credentials: 'include',
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers || {}),
    },
  });
  const json = (await res.json()) as T & { ok?: boolean; error?: string; action?: { label: string; href: string } };
  if (!res.ok) {
    const err = new Error(json.error || `Request failed (${res.status})`) as Error & {
      action?: { label: string; href: string };
    };
    err.action = json.action;
    throw err;
  }
  return json;
}

export async function fetchAnalyticsSummary(days = 14): Promise<AnalyticsSummary> {
  try {
    const json = await adminFetch<{ ok: boolean; summary: AnalyticsSummary }>(
      `/api/admin/analytics/summary?days=${days}`
    );
    return json.summary;
  } catch (e) {
    if (AUTH_CONFIG.mock) return buildLocalAnalyticsSummary(days);
    throw e;
  }
}

export async function fetchPricingSubheadExperiment(
  days = 14
): Promise<PricingSubheadExperimentSummary> {
  try {
    const json = await adminFetch<{ ok: boolean; experiment: PricingSubheadExperimentSummary }>(
      `/api/admin/experiments/pricing-subhead?days=${days}`
    );
    return json.experiment;
  } catch (e) {
    if (AUTH_CONFIG.mock) return buildPricingSubheadExperimentSummary(days);
    throw e;
  }
}

export async function interpretAnalyticsInsight(days = 14): Promise<{
  insight: AiAnalyticsInsight;
  summary: AnalyticsSummary;
}> {
  return adminFetch(`/api/admin/analytics/interpret`, {
    method: 'POST',
    body: JSON.stringify({ days }),
  });
}

export async function diagnoseWithEngineer(payload: {
  errors: { message: string; timestamp?: string; path?: string }[];
  health?: unknown;
  logs?: { level: string; message: string }[];
}): Promise<EngineerDiagnosis> {
  const json = await adminFetch<{ ok: boolean; diagnosis: EngineerDiagnosis }>(
    '/api/admin/engineer/diagnose',
    { method: 'POST', body: JSON.stringify(payload) }
  );
  return json.diagnosis;
}

export async function createEngineerJob(
  diagnosis: EngineerDiagnosis,
  dryRun = true
): Promise<EngineerJob> {
  const json = await adminFetch<{ ok: boolean; job: EngineerJob }>('/api/admin/engineer/jobs', {
    method: 'POST',
    body: JSON.stringify({ diagnosis, dryRun }),
  });
  return json.job;
}

export async function pushEngineerJob(jobId: string): Promise<{ job: EngineerJob; message: string }> {
  return adminFetch(`/api/admin/engineer/jobs/${jobId}/push`, { method: 'POST', body: '{}' });
}

export async function listEngineerJobs(): Promise<EngineerJob[]> {
  const json = await adminFetch<{ ok: boolean; jobs: EngineerJob[] }>('/api/admin/engineer/jobs');
  return json.jobs;
}
